/**
 * React Query configuration for StudySmith webapp.
 * Provides caching, background updates, and optimistic updates.
 */

import { QueryClient } from '@tanstack/react-query';

// Create and configure the query client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      
      // Retry failed requests
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Refetch on window focus for important data
      refetchOnWindowFocus: true,
      
      // Don't refetch on reconnect by default (can be overridden per query)
      refetchOnReconnect: true,
      
      // Background refetch interval (30 seconds for active queries)
      refetchInterval: false, // Disabled by default, enable per query if needed
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Query keys factory for consistent cache key management
export const queryKeys = {
  // Subjects
  subjects: {
    all: ['subjects'],
    lists: () => [...queryKeys.subjects.all, 'list'],
    list: (filters) => [...queryKeys.subjects.lists(), filters],
    details: () => [...queryKeys.subjects.all, 'detail'],
    detail: (id) => [...queryKeys.subjects.details(), id],
    stats: (id) => [...queryKeys.subjects.detail(id), 'stats'],
  },
  
  // Guides
  guides: {
    all: ['guides'],
    bySubject: (subjectId) => [...queryKeys.guides.all, 'subject', subjectId],
    generation: (subjectId, jobId) => [...queryKeys.guides.bySubject(subjectId), 'generation', jobId],
  },
  
  // Files
  files: {
    all: ['files'],
    bySubject: (subjectId) => [...queryKeys.files.all, 'subject', subjectId],
    detail: (fileId) => [...queryKeys.files.all, 'detail', fileId],
  },
  
  // User
  user: {
    profile: ['user', 'profile'],
    settings: ['user', 'settings'],
  }
};

// Cache invalidation utilities
export const invalidateQueries = {
  // Invalidate all subject-related queries
  subjects: () => queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all }),
  
  // Invalidate specific subject
  subject: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.subjects.detail(id) }),
  
  // Invalidate subject guides
  subjectGuides: (subjectId) => queryClient.invalidateQueries({ 
    queryKey: queryKeys.guides.bySubject(subjectId) 
  }),
  
  // Invalidate subject files
  subjectFiles: (subjectId) => queryClient.invalidateQueries({ 
    queryKey: queryKeys.files.bySubject(subjectId) 
  }),
};

// Optimistic update utilities
export const optimisticUpdates = {
  // Update subject in cache optimistically
  updateSubject: (subjectId, updateData) => {
    queryClient.setQueryData(
      queryKeys.subjects.detail(subjectId),
      (oldData) => oldData ? { ...oldData, ...updateData } : oldData
    );
  },
  
  // Add new subject optimistically
  addSubject: (newSubject) => {
    queryClient.setQueryData(
      queryKeys.subjects.lists(),
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          subjects: [newSubject, ...oldData.subjects],
          total_count: oldData.total_count + 1
        };
      }
    );
  },
  
  // Remove subject optimistically
  removeSubject: (subjectId) => {
    queryClient.setQueryData(
      queryKeys.subjects.lists(),
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          subjects: oldData.subjects.filter(s => s.id !== subjectId),
          total_count: Math.max(0, oldData.total_count - 1)
        };
      }
    );
  }
};

// Prefetch utilities for performance
export const prefetchQueries = {
  // Prefetch subject details when hovering over subject list items
  subjectDetails: async (subjectId) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.subjects.detail(subjectId),
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  },
  
  // Prefetch guide when navigating to subject
  subjectGuide: async (subjectId) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.guides.bySubject(subjectId),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  }
};

export default queryClient;